/**
 * Recording Service - Save and manage reading recordings
 * Uses type-safe transformers to convert DB data to app types
 */

import { supabase } from '../../../lib/supabase/client';
import { transformRecording, transformRecordings } from '../../../lib/supabase/index';
import type { Recording } from '../../../lib/supabase/transforms';

// Re-export type for component use
export type { Recording as ReadingRecording };

export interface SaveRecordingParams {
  storybookId: string;
  audioBlob: Blob;
  durationSeconds: number;
  childName?: string;
  notes?: string;
}

/**
 * Save a reading recording
 */
export async function saveReadingRecording(params: SaveRecordingParams): Promise<{
  success: boolean;
  recording?: Recording;
  error?: string;
}> {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    // Get user's profile
    const { data: profile } = await supabase
      .from('profiles')
      .select('id')
      .eq('user_id', user.id)
      .maybeSingle();

    if (!profile) {
      return { success: false, error: 'Profile not found' };
    }

    // Generate unique filename
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `recordings/${profile.id}/${params.storybookId}/${timestamp}.webm`;

    // Upload to storage
    const { error: uploadError } = await supabase.storage
      .from('audio')
      .upload(filename, params.audioBlob, {
        contentType: 'audio/webm',
        upsert: false,
      });

    if (uploadError) {
      return { success: false, error: `Upload failed: ${uploadError.message}` };
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('audio')
      .getPublicUrl(filename);

    // Save recording metadata
    const { data: recording, error: insertError } = await supabase
      .from('reading_recordings')
      .insert({
        storybook_id: params.storybookId,
        parent_id: profile.id,
        audio_url: urlData.publicUrl,
        duration_seconds: params.durationSeconds,
        child_name: params.childName || null,
        notes: params.notes || null,
        recorded_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (insertError) {
      return { success: false, error: `Save failed: ${insertError.message}` };
    }

    return { success: true, recording: transformRecording(recording) };
  } catch (err) {
    console.error('Error saving recording:', err);
    return { success: false, error: 'Unexpected error' };
  }
}

/**
 * Get recordings for a storybook
 */
export async function getRecordingsForStorybook(storybookId: string): Promise<Recording[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data: profile } = await supabase
    .from('profiles')
    .select('id')
    .eq('user_id', user.id)
    .maybeSingle();

  if (!profile) return [];

  const { data } = await supabase
    .from('reading_recordings')
    .select('*')
    .eq('storybook_id', storybookId)
    .eq('parent_id', profile.id)
    .order('recorded_at', { ascending: false });

  return transformRecordings(data || []);
}

/**
 * Toggle favorite status
 */
export async function toggleRecordingFavorite(recordingId: string, isFavorite: boolean): Promise<boolean> {
  const { error } = await supabase
    .from('reading_recordings')
    .update({ is_favorite: isFavorite })
    .eq('id', recordingId);

  return !error;
}

/**
 * Delete a recording
 */
export async function deleteRecording(recordingId: string): Promise<boolean> {
  // Get the recording to find the audio URL
  const { data: recording } = await supabase
    .from('reading_recordings')
    .select('audio_url')
    .eq('id', recordingId)
    .maybeSingle();

  if (recording?.audio_url) {
    // Extract path from URL and delete from storage
    const url = new URL(recording.audio_url);
    const path = url.pathname.split('/storage/v1/object/public/audio/')[1];
    if (path) {
      await supabase.storage.from('audio').remove([path]);
    }
  }

  // Delete the record
  const { error } = await supabase
    .from('reading_recordings')
    .delete()
    .eq('id', recordingId);

  return !error;
}
